import {
  useMutation,
  useQueryClient,
  UseMutationOptions,
} from '@tanstack/react-query';
import { CreateChildOrganization, Organization } from '@tinct/core-api-models';
import { ApiError, tinctApi } from '../../api';

type CreateChildOrganizationVariables = {
  agencyId: string;
  data: CreateChildOrganization;
  copyMembers?: boolean;
};

export const createChildOrganization = async (
  variables: CreateChildOrganizationVariables,
): Promise<Organization> => {
  return await tinctApi.organizations.createChildOrganization(
    variables.agencyId,
    variables.data,
    variables.copyMembers,
  );
};

/** Agency admin: creates a child `business` workspace managed by the agency. */
export const useCreateChildOrganization = (
  options?: Omit<
    UseMutationOptions<Organization, ApiError, CreateChildOrganizationVariables>,
    'mutationFn'
  >,
) => {
  const { onSuccess, ...restOptions } = options || {};
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createChildOrganization,
    onSuccess: (...args) => {
      // Children are listed from the user's organizations, and the agency's
      // `workspace_count` quota moved — refresh both.
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      queryClient.invalidateQueries({ queryKey: ['organization'] });
      onSuccess?.(...args);
    },
    ...restOptions,
  });
};
